import { Component, OnInit, NgZone, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

const STORAGE_KEY_DISMISSED = 'pwa-install-dismissed';
const STORAGE_KEY_VISITS = 'pwa-visit-count';
const REMINDER_VISIT_THRESHOLD = 5;

// Event global abfangen — feuert oft bevor Angular initialisiert ist
(window as any).__pwaInstallPrompt = null;
window.addEventListener('beforeinstallprompt', (e: Event) => {
	e.preventDefault();
	(window as any).__pwaInstallPrompt = e;
});

@Component({
	standalone: true,
	imports: [CommonModule],
	selector: 'bp-pwa-install-banner',
	template: `
		<div *ngIf="showBanner" class="pwa-banner">
			<div class="pwa-banner-content">
				<div class="pwa-banner-text">
					<strong>BpWeb funktioniert am besten als installierte App.</strong>
					<button *ngIf="canInstall" class="pwa-install-btn" (click)="install()">Jetzt installieren</button>
					<span *ngIf="!canInstall" class="pwa-banner-detail">{{ fallbackHint }}</span>
				</div>
				<button class="pwa-banner-close" (click)="dismiss()" title="Schließen">&times;</button>
			</div>
		</div>
	`,
	styles: [`
		.pwa-banner {
			background: #2f4f4f;
			color: white;
			padding: 8px 16px;
			font-size: 0.85rem;
			z-index: 1050;
		}
		.pwa-banner-content {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 12px;
			max-width: 1200px;
			margin: 0 auto;
		}
		.pwa-banner-text {
			display: flex;
			flex-wrap: wrap;
			gap: 4px 8px;
			align-items: center;
		}
		.pwa-banner-detail {
			opacity: 0.9;
		}
		.pwa-install-btn {
			background: white;
			color: #2f4f4f;
			border: none;
			border-radius: 4px;
			padding: 4px 12px;
			font-size: 0.85rem;
			font-weight: 500;
			cursor: pointer;
		}
		.pwa-install-btn:hover {
			background: #e0e0e0;
		}
		.pwa-banner-close {
			background: none;
			border: none;
			color: white;
			font-size: 1.3rem;
			cursor: pointer;
			padding: 0 4px;
			line-height: 1;
			opacity: 0.8;
			flex-shrink: 0;
		}
		.pwa-banner-close:hover {
			opacity: 1;
		}
	`]
})
export class PwaInstallBannerComponent implements OnInit {
	showBanner = false;
	canInstall = false;
	fallbackHint = '';

	private deferredPrompt: any = null;
	private ngZone = inject(NgZone);

	private isStandalone(): boolean {
		return window.matchMedia('(display-mode: standalone)').matches
			|| (navigator as any).standalone === true;
	}

	ngOnInit(): void {
		if (this.isStandalone()) return;

		// Prüfen ob beforeinstallprompt bereits gefeuert hat (global abgefangen)
		if ((window as any).__pwaInstallPrompt) {
			this.deferredPrompt = (window as any).__pwaInstallPrompt;
			this.canInstall = true;
			this.showBannerIfNeeded();
			return;
		}

		// Falls noch nicht gefeuert: auf spätes Event warten
		window.addEventListener('beforeinstallprompt', (e: Event) => {
			e.preventDefault();
			this.ngZone.run(() => {
				this.deferredPrompt = e;
				this.canInstall = true;
				this.showBannerIfNeeded();
			});
		});

		// Fallback für Browser ohne beforeinstallprompt (Firefox, Safari)
		setTimeout(() => {
			if (!this.deferredPrompt) {
				this.fallbackHint = 'Nutzen Sie Chrome oder Edge, um BpWeb als App zu installieren.';
				this.showBannerIfNeeded();
			}
		}, 3000);
	}

	private showBannerIfNeeded(): void {
		if (this.showBanner) return;

		const dismissed = localStorage.getItem(STORAGE_KEY_DISMISSED);
		const visits = parseInt(localStorage.getItem(STORAGE_KEY_VISITS) || '0', 10) + 1;
		localStorage.setItem(STORAGE_KEY_VISITS, String(visits));

		if (!dismissed) {
			this.showBanner = true;
		} else if (visits % REMINDER_VISIT_THRESHOLD === 0) {
			this.showBanner = true;
		}
	}

	async install(): Promise<void> {
		if (!this.deferredPrompt) return;

		this.deferredPrompt.prompt();
		const result = await this.deferredPrompt.userChoice;

		if (result.outcome === 'accepted') {
			this.showBanner = false;
			localStorage.setItem(STORAGE_KEY_DISMISSED, 'true');
		}
		this.deferredPrompt = null;
		this.canInstall = false;
	}

	dismiss(): void {
		this.showBanner = false;
		localStorage.setItem(STORAGE_KEY_DISMISSED, 'true');
	}
}
